import type {
  ObjectRepeatType,
  ObjectSizeType,
} from "@v1/components/editor/BackgroundAssetPicker";
import type { SolidOrGradient } from "replo-runtime/shared/types";
import type { GradientStop } from "schemas/styleAttribute";

import * as React from "react";

import ErrorMessage from "@v1/components/common/ErrorMessage/ErrorMessage";
import Popover from "@v1/components/designSystem/popover/Popover";
import BackgroundAssetPicker from "@v1/components/editor/BackgroundAssetPicker";
import { BADGE_TRIGGER_OFFSET } from "@v1/components/editor/constants";
import DocumentationInfoIcon from "@v1/components/editor/page/element-editor/components/DocumentationInfoIcon";
import DynamicDataValueIndicator from "@v1/components/editor/page/element-editor/components/extras/DynamicDataValueIndicator";
import ModifierGroup from "@v1/components/editor/page/element-editor/components/extras/ModifierGroup";
import ModifierLabel from "@v1/components/editor/page/element-editor/components/extras/ModifierLabel";
import DynamicColorModifier from "@v1/components/editor/page/element-editor/components/modifiers/DynamicColorModifier";
import InlineAssetSelector from "@v1/components/editor/page/element-editor/components/modifiers/InlineAssetSelector";
import { hasDynamicData } from "@v1/components/editor/page/element-editor/components/modifiers/utils";
import { useGetModifierControls } from "@v1/hooks/rightBar/useGetModifierControls";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import {
  selectBackgroundColor,
  selectBackgroundGradientStops,
  selectBackgroundGradientTilt,
  selectBackgroundImage,
  selectBackgroundPositionX,
  selectBackgroundPositionY,
  selectBackgroundRepeat,
  selectBackgroundSize,
  selectDraftComponentContext,
  selectDraftComponentId,
} from "@v1/reducers/core-reducer";
import { useEditorSelector } from "@v1/store/store";
import { isImageSourceValid } from "@v1/utils/image";
import { useDynamicCommandMenuItem } from "@v2/hooks/useDynamicCommandMenuItems";
import { useAreModalsOpen } from "@v2/store/modalStore";
import { Image, PaintBucket } from "lucide-react";
import { isDynamicDataValue } from "replo-runtime";
import { evaluateVariableAsString } from "replo-runtime/store/ReploVariable";
import {
  isMixedStyleValue,
  REPLO_MIXED_STYLE_VALUE,
} from "replo-runtime/store/utils/mixed-values";
import { coerceNumberToString } from "replo-utils/lib/misc";

type BackgroundPopoverId =
  | "image-source-selector"
  | "background-color-selector"
  | null;

export const BackgroundModifier: React.FC = () => {
  const { src } = useBackgroundImage();
  const backgroundColor = useEditorSelector(selectBackgroundColor);
  const groupRef = React.useRef<HTMLDivElement>(null);
  const [controls, addControl] = useGetModifierControls<"background">(
    "background",
    groupRef,
  );
  const [openPopoverId, setOpenPopoverId] =
    React.useState<BackgroundPopoverId>(null);

  const hasControlsToRender = controls.size > 0;

  const onSelectBgColor = React.useCallback(() => {
    setOpenPopoverId("background-color-selector");
    addControl("backgroundColor");
  }, [addControl]);

  const backgroundColorDynamicMenuItem = React.useMemo(() => {
    return !controls.has("backgroundColor")
      ? ({
          label: "Add Background Color",
          group: "add",
          icon: { type: "iconComponent", component: PaintBucket },
          onSelect: onSelectBgColor,
        } as const)
      : null;
  }, [controls, onSelectBgColor]);

  useDynamicCommandMenuItem("backgroundColor", backgroundColorDynamicMenuItem);

  const onSelectBgImage = React.useCallback(() => {
    setOpenPopoverId("image-source-selector");
    addControl("backgroundImage");
  }, [addControl]);

  const backgroundImageDynamicMenuItem = React.useMemo(() => {
    return !controls.has("backgroundImage")
      ? ({
          label: "Add Background Image",
          group: "add",
          icon: { type: "iconComponent", component: Image },
          onSelect: onSelectBgImage,
        } as const)
      : null;
  }, [controls, onSelectBgImage]);

  useDynamicCommandMenuItem("backgroundImage", backgroundImageDynamicMenuItem);

  return (
    <ModifierGroup
      ref={groupRef}
      title="Background"
      tooltipText="Add Background"
      titleEnhancer={<DocumentationInfoIcon documentationType="background" />}
      isDefaultOpen={
        Boolean(src) || Boolean(backgroundColor) || hasControlsToRender
      }
      isCollapsible={hasControlsToRender}
      menuItems={[
        {
          id: "color",
          title: "Color",
          onSelect: () => {
            setOpenPopoverId("background-color-selector");
            addControl("backgroundColor");
          },
          type: "leaf",
          disabled: controls.has("backgroundColor"),
          variant: "default",
          size: "sm",
        },
        {
          id: "image",
          title: "Image",
          onSelect: () => {
            addControl("backgroundImage");
            setOpenPopoverId("image-source-selector");
          },
          type: "leaf",
          disabled: controls.has("backgroundImage"),
          variant: "default",
          size: "sm",
        },
      ]}
    >
      {hasControlsToRender && (
        <div className="flex w-full flex-col gap-1">
          {controls.has("backgroundImage") && (
            <BackgroundImageControl
              isOpen={openPopoverId === "image-source-selector"}
              setIsOpen={(isOpen) => {
                return setOpenPopoverId(
                  isOpen ? "image-source-selector" : null,
                );
              }}
            />
          )}
          {controls.has("backgroundColor") && (
            <BackgroundColorControl
              isOpen={openPopoverId === "background-color-selector"}
              setIsOpen={(isOpen) => {
                return setOpenPopoverId(
                  isOpen ? "background-color-selector" : null,
                );
              }}
            />
          )}
        </div>
      )}
    </ModifierGroup>
  );
};

const BackgroundImageControl: React.FC<{
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}> = ({ isOpen, setIsOpen }) => {
  const draftComponentId = useEditorSelector(selectDraftComponentId);
  const backgroundSize = useEditorSelector(
    selectBackgroundSize,
  ) as ObjectSizeType;
  const backgroundRepeat = useEditorSelector(
    selectBackgroundRepeat,
  ) as ObjectRepeatType;
  const backgroundPositionX = useEditorSelector(selectBackgroundPositionX);
  const backgroundPositionY = useEditorSelector(selectBackgroundPositionY);
  const areModalsOpen = useAreModalsOpen();
  const applyComponentAction = useApplyComponentAction();
  const { src, assetSrc, removeImageSrc } = useBackgroundImage();

  const openPopover = () => {
    setIsOpen(true);
  };

  return (
    <Popover.Root isOpen={isOpen} onOpenChange={setIsOpen}>
      <div className="flex flex-col gap-1">
        <div className="flex w-full items-center">
          <ModifierLabel label="Source" />
          {typeof src === "string" && isDynamicDataValue(src) ? (
            <DynamicDataValueIndicator
              type="color"
              templateValue={src}
              onClick={openPopover}
              onRemove={removeImageSrc}
              componentId={draftComponentId ?? undefined}
            />
          ) : (
            <InlineAssetSelector
              size="sm"
              emptyTitle="Choose Image"
              onRemoveAsset={removeImageSrc}
              allowRemoveAsset
              allowsDynamicData={hasDynamicData(draftComponentId)}
              isImageOnly
              onClickSelectAsset={openPopover}
              asset={
                isMixedStyleValue(assetSrc)
                  ? assetSrc
                  : {
                      type: "image",
                      src: assetSrc!,
                    }
              }
              onChange={(url) => {
                return applyComponentAction({
                  type: "setStyles",
                  value: {
                    // Note (Gabe, 2025-02-24): We need to use double quotes to
                    // ensure data:image sources work as they may contain single
                    // quotes.
                    backgroundImage: isDynamicDataValue(url)
                      ? url
                      : `url("${url}")`,
                    backgroundSize: "cover",
                  },
                });
              }}
              shouldOpenLibraryPopover={false}
            />
          )}
        </div>
        {!isMixedStyleValue(src) && !isImageSourceValid(src ?? null) && (
          <ErrorMessage error="Please enter a valid image source" />
        )}
      </div>
      <Popover.Anchor className="relative left-0 top-0" />
      {isOpen && (
        <Popover.Content
          title="Choose Image"
          shouldPreventDefaultOnInteractOutside={areModalsOpen}
        >
          <BackgroundAssetPicker
            url={assetSrc ?? undefined}
            backgroundSizeValue={backgroundSize}
            onChangeSource={(url: string) => {
              applyComponentAction({
                type: "setStyles",
                value: {
                  // Note (Gabe, 2025-02-24): We need to use double quotes to
                  // ensure data:image sources work as they may contain single
                  // quotes.
                  backgroundImage: `url("${url}")`,
                  backgroundSize: "cover",
                },
              });
            }}
            onChangeBackgroundSize={(value: any) => {
              applyComponentAction({
                type: "setStyles",
                value: { backgroundSize: value },
              });
            }}
            backgroundPositionValue={{
              x: backgroundPositionX ? String(backgroundPositionX) : undefined,
              y: backgroundPositionY ? String(backgroundPositionY) : undefined,
            }}
            onChangeBackgroundPositionX={(value: any) => {
              applyComponentAction({
                type: "setStyles",
                value: { backgroundPositionX: value },
              });
            }}
            onChangeBackgroundPositionY={(value: any) => {
              applyComponentAction({
                type: "setStyles",
                value: { backgroundPositionY: value },
              });
            }}
            backgroundRepeatValue={backgroundRepeat ?? "repeat"}
            onChangeBackgroundRepeat={(value: any) => {
              applyComponentAction({
                type: "setStyles",
                value: { backgroundRepeat: value },
              });
            }}
            allowsSettingDynamicData
          />
        </Popover.Content>
      )}
    </Popover.Root>
  );
};

const BackgroundColorControl: React.FC<{
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}> = ({ isOpen, setIsOpen }) => {
  const applyComponentAction = useApplyComponentAction();
  const backgroundColor = useEditorSelector(selectBackgroundColor);
  const backgroundGradientTilt = useEditorSelector(
    selectBackgroundGradientTilt,
  );
  const backgroundGradientStops = useEditorSelector(
    selectBackgroundGradientStops,
  ) as GradientStop[];

  const isMixedGradient =
    isMixedStyleValue(backgroundGradientTilt) ||
    isMixedStyleValue(backgroundGradientStops);

  return (
    <div className="flex items-center">
      <ModifierLabel label="Color" />
      <DynamicColorModifier
        isPopoverOpen={isOpen}
        onOpenPopoverChange={setIsOpen}
        previewProperty="backgroundColor"
        gradientSelectionType="backgroundColor"
        gradientData={
          isMixedGradient
            ? REPLO_MIXED_STYLE_VALUE
            : {
                tilt: coerceNumberToString(backgroundGradientTilt) ?? "90deg",
                stops: backgroundGradientStops ?? [
                  {
                    id: "c7795a8c-4e13-4011-889b-64adb0e11e41",
                    color: "#df9393",
                    location: "0%",
                  },
                ],
              }
        }
        field="style.backgroundColor"
        value={backgroundColor ?? undefined}
        popoverTitle="Background Color"
        popoverSideOffset={BADGE_TRIGGER_OFFSET}
        onChange={(value: SolidOrGradient) => {
          if (typeof value === "string" && isDynamicDataValue(value)) {
            applyComponentAction({
              type: "setStyles",
              value: {
                backgroundColor: value,
                __alchemyGradient__backgroundColor__tilt: null,
                __alchemyGradient__backgroundColor__stops: null,
                __reploGradient__color__design_library: null,
              },
            });
            return;
          }
          const solidOrGradient = value;
          if (solidOrGradient.type === "solid") {
            applyComponentAction({
              type: "setStyles",
              value: {
                backgroundColor: solidOrGradient.color,
                // NOTE (Fran 2024-11-27): Remove gradient data when setting solid color
                __alchemyGradient__backgroundColor__tilt: null,
                __alchemyGradient__backgroundColor__stops: null,
                __reploGradient__color__design_library: null,
              },
            });
          } else {
            const gradient = solidOrGradient.gradient;
            applyComponentAction({
              type: "setStyles",
              value: {
                backgroundColor: "alchemy:gradient",
                __alchemyGradient__backgroundColor__tilt: gradient.tilt,
                __alchemyGradient__backgroundColor__stops: gradient.stops,
                // NOTE (Fran 2024-11-27): Remove gradient data from design library when setting gradient color
                __reploGradient__color__design_library: null,
              },
            });
          }
        }}
        showSavedStyles
      />
    </div>
  );
};

function useBackgroundImage() {
  const applyComponentAction = useApplyComponentAction();
  const draftComponentContext = useEditorSelector(selectDraftComponentContext);
  const rawBackgroundImage = useEditorSelector(selectBackgroundImage);

  const removeImageSrc = React.useCallback(() => {
    applyComponentAction({
      type: "setStyles",
      value: { backgroundImage: "" },
    });
  }, [applyComponentAction]);

  if (isMixedStyleValue(rawBackgroundImage)) {
    return {
      src: rawBackgroundImage,
      assetSrc: rawBackgroundImage,
      removeImageSrc,
    };
  }

  const backgroundImage =
    rawBackgroundImage &&
    rawBackgroundImage?.length > 0 &&
    !isDynamicDataValue(rawBackgroundImage)
      ? rawBackgroundImage.slice(5, -2)
      : rawBackgroundImage;

  const assetSrc =
    isDynamicDataValue(backgroundImage ?? undefined) && draftComponentContext
      ? evaluateVariableAsString(backgroundImage!, draftComponentContext)
      : backgroundImage;

  return {
    src: backgroundImage,
    assetSrc,
    removeImageSrc,
  };
}
